import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { IoIosEyeOff, IoIosEye } from "react-icons/io";
import Cookies from "js-cookie";

import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { showToast } from "../../utils/toast";
import { apiClient } from "../../services/api";
import { useAppState, RESTORE_SESSION } from "../../contexts/AppContext";
import { ApiRoutes, token, refreshToken, routePaths } from "../../constants";
import { AppRoutes } from "../../utils";
import { getUserRoleFromToken } from "../../utils/user";
import { UserRole } from "../../services/queries/users/types";
import { parseError } from "../../utils";

interface FormProps {
  email: string;
  password: string;
}

export const Route = createFileRoute("/_auth/login")({
  component: Login,
});

function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { dispatch, state } = useAppState();

  const navigate = useNavigate();
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormProps>({ mode: "onSubmit", reValidateMode: "onChange" });

  const onSubmit: SubmitHandler<FormProps> = async ({ email, password }) => {
    setIsLoading(true);

    try {
      const { data } = await apiClient.post({
        url: ApiRoutes.LOGIN,
        body: { email, password },
      });
      Cookies.set(token, data.accessToken);
      Cookies.set(refreshToken, data.refreshToken);
      const businessUserRole = getUserRoleFromToken(data.accessToken);
      dispatch({
        type: RESTORE_SESSION,
        payload: {
          authToken: data.accessToken,
          businessUserRole: businessUserRole as UserRole,
          user: data.user,
          business: data.business,
        },
      });
    } catch (error: unknown) {
      const message = parseError(error);
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!state.user?.id || !state.business?.id) return;

    navigate({ to: AppRoutes.DASHBOARD });
  }, [state.user?.id, state.business?.id, navigate]);

  return (
    <div className="">
      <h1 className="uppercase text-center text-gray-600">
        log in to your account
      </h1>

      <form className="my-6" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form-control mb-8">
          <TextInput
            className="w-full px-6 py-3 rounded-lg"
            {...register("email", { required: "Email Address is required " })}
            placeholder="Email"
            error={errors.email}
          />
        </div>

        <div className="form-control mb-5 relative">
          <TextInput
            type={showPassword ? "text" : "password"}
            className="w-full px-6 py-3 rounded-lg"
            {...register("password", { required: "Password is required" })}
            placeholder="Password"
            error={errors.password}
          />
          <div
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
          >
            {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
          </div>
        </div>

        <div className="flex justify-end mb-5 cursor-pointer text-xs">
          <Link className="text-primary text-sm" to={routePaths.forgotPassword}>
            Forgot your password?
          </Link>
        </div>

        <Button
          className="bg-primary hover:bg-primary/80 transition-colors text-white w-full py-3 mb-5 rounded-lg border-primary"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Login"}
        </Button>

        <div className="text-gray-500 text-sm text-center mb-4">
          Don't have an account?
          <Link to={routePaths.signup} className="text-primary text-sm ml-1">
            Create account
          </Link>
        </div>
      </form>
    </div>
  );
}
